import { MutableRefObject, RefObject, useMemo, useRef } from 'react';
import { Camera, Scene, WebGLRenderer } from 'three';

import { isDefined } from '../../../../utils/variables';
import { RenderItem, useAnimate } from '../../../hooks/three/use-animate';
import { useCamera } from '../../../hooks/three/use-camera';
import { useWebGLRenderer } from '../../../hooks/three/use-webgl-renderer';
import { useWindowResize } from '../../../hooks/three/use-window-resize';

export type SfmRenderingContextOptions = {
  /** Ref to callback run each frame before rendering. Set by useWms so the ortho tile asset is updated. */
  onBeforeRenderRef?: MutableRefObject<
    ((camera: Camera, renderer: WebGLRenderer) => void) | undefined
  >;
};

export const useSfmRenderingContext = (
  webGlContainerRef: RefObject<HTMLDivElement>,
  options?: SfmRenderingContextOptions,
) => {
  const webGlRenderer = useWebGLRenderer(webGlContainerRef);
  const { camera, setCameraType } = useCamera(webGlContainerRef);
  const webGlScene = useRef(new Scene());
  const onBeforeRenderRef = options?.onBeforeRenderRef;

  const renderItems = useMemo<RenderItem[]>(() => {
    if (!isDefined(webGlRenderer)) {
      return [];
    }

    webGlRenderer.autoClear = false;
    const beforeRenderRef = onBeforeRenderRef;

    return [
      {
        render: (camera) => {
          webGlRenderer.state.reset();
          webGlRenderer.clear();
          beforeRenderRef?.current?.(camera, webGlRenderer);
          webGlRenderer.render(webGlScene.current, camera);
        },
        renderer: webGlRenderer,
      },
    ];
  }, [webGlRenderer, onBeforeRenderRef]);

  useAnimate(renderItems, camera);
  useWindowResize(renderItems, webGlContainerRef, camera);

  return {
    webGlScene: webGlScene.current,
    camera,
    setCameraType,
  };
};
